import { useState } from 'react';
import { motion } from 'framer-motion';
import { X, MessageCircle, ArrowLeft, Check, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatarPreco, emOferta, copiarTexto } from '../data/produtos';

export default function ProductModal({ produto, onFechar, onWhatsApp }) {
  const [fotoAtiva, setFotoAtiva] = useState(0);
  const fotos = produto.fotos && produto.fotos.length > 0 ? produto.fotos : [produto.imagem];
  const copiarLink = async () => {
    const ok = await copiarTexto(`${window.location.origin}/produto/${produto.id}`);
    if (ok) toast.success('Link do produto copiado!');
    else toast.error('Não foi possível copiar o link.');
  };
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-mk-bluedark/70 backdrop-blur-sm p-0 sm:p-6"
      onClick={onFechar}
    >
      <motion.div
        data-testid="product-modal"
        initial={{ opacity: 0, y: 60, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 40, scale: 0.98 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="relative grid max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-t-3xl sm:rounded-3xl bg-white shadow-2xl sm:grid-cols-2"
      >
        <button
          data-testid="product-modal-close-button"
          onClick={onFechar}
          aria-label="Fechar detalhes"
          className="absolute right-3 top-3 z-10 rounded-full bg-white/90 p-2 text-slate-500 shadow-md transition-colors hover:bg-slate-100 hover:text-slate-800"
        >
          <X size={18} />
        </button>

        <div className="relative bg-slate-50">
          {produto.esgotado ? (
            <span className="absolute left-3 top-3 z-10 rounded-full bg-slate-700 px-3 py-1 text-[11px] font-extrabold uppercase tracking-widest text-white shadow-lg">
              Esgotado
            </span>
          ) : (
            emOferta(produto) && (
              <span className="absolute left-3 top-3 z-10 rounded-full bg-mk-red px-3 py-1 text-[11px] font-extrabold uppercase tracking-widest text-white shadow-lg">
                Oferta
              </span>
            )
          )}
          <img
            data-testid="product-modal-image-zoom"
            src={fotos[fotoAtiva]}
            alt={produto.nome}
            className="h-64 w-full object-cover sm:h-full"
          />
          {fotos.length > 1 && (
            <div className="absolute bottom-3 left-1/2 z-10 flex -translate-x-1/2 gap-2 rounded-2xl bg-white/90 p-2 shadow-lg backdrop-blur">
              {fotos.map((foto, i) => (
                <button
                  key={foto + i}
                  data-testid={`product-modal-thumb-${i}`}
                  onClick={() => setFotoAtiva(i)}
                  aria-label={`Foto ${i + 1}`}
                  className={`h-12 w-12 overflow-hidden rounded-xl border-2 transition-colors ${i === fotoAtiva ? 'border-mk-blue' : 'border-transparent opacity-70 hover:opacity-100'}`}
                >
                  <img src={foto} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-col p-5 sm:p-7">
          {produto.marca && (
            <p className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-mk-blue">{produto.marca}</p>
          )}
          <h2 className="mt-1.5 font-heading text-xl sm:text-2xl font-extrabold leading-tight text-slate-900">
            {produto.nome}
          </h2>

          <ul className="mt-4 space-y-2">
            {produto.detalhes.map((d) => (
              <li key={d} className="flex items-center gap-2 text-sm text-slate-600">
                <Check size={15} className="shrink-0 text-mk-green" strokeWidth={3} />
                {d}
              </li>
            ))}
          </ul>

          <p className="mt-4 text-sm leading-relaxed text-slate-500">{produto.descricao}</p>

          <div className="mt-5 rounded-2xl bg-slate-50 p-4">
            {produto.parcelas && (
              <p className="text-xs font-medium text-slate-500">
                {produto.parcelas.vezes}x de {formatarPreco(produto.parcelas.valor)}
              </p>
            )}
            {produto.precoDe && (
              <p className="mt-1 text-sm font-medium text-slate-400 line-through">
                de {formatarPreco(produto.precoDe)}
              </p>
            )}
            <p className="mt-1 flex items-baseline gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">à vista</span>
              <span className="font-heading text-3xl font-black tracking-tight text-mk-red">
                {formatarPreco(produto.preco)}
              </span>
            </p>
            {produto.estoqueExibido != null && !produto.esgotado && (
              <p data-testid="product-modal-stock" className="mt-2 text-sm font-bold text-mk-blue">
                {produto.estoqueExibido} unidades em estoque
              </p>
            )}
          </div>

          <div className="mt-5 flex flex-col gap-2.5">
            <button
              data-testid="product-modal-whatsapp-button"
              onClick={() => onWhatsApp(produto)}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-mk-green px-5 py-3.5 text-sm sm:text-base font-bold text-white shadow-lg shadow-mk-green/30 transition-colors duration-300 hover:bg-mk-greendark"
            >
              <MessageCircle size={19} strokeWidth={2.5} />
              Consultar pelo WhatsApp
            </button>
            <button
              data-testid="product-modal-back-button"
              onClick={onFechar}
              className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold text-mk-blue transition-colors hover:bg-blue-50"
            >
              <ArrowLeft size={16} />
              Voltar ao catálogo
            </button>
            <button
              data-testid="product-modal-copy-link-button"
              onClick={copiarLink}
              className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold text-slate-500 transition-colors hover:bg-slate-100"
            >
              <Link2 size={16} />
              Copiar link do produto
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
